import { useEffect, useId, useMemo, useRef, useState, type FocusEvent, type KeyboardEvent } from "react";
import { findCities, formatCity, type CityOption } from "../cities";
import { reverseGeocodeCity } from "../search";
import { Icon } from "./Icon";

interface CityComboboxProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  autoFocus?: boolean;
  required?: boolean;
  className?: string;
}

function locationErrorMessage(error: GeolocationPositionError): string {
  if (error.code === 1) return "Location wasn’t shared. Search and choose a city instead.";
  if (error.code === 2) return "Your location is unavailable. Search and choose a city instead.";
  if (error.code === 3) return "Location took too long. Try again or choose a city.";
  return "Roam could not read your location. Search and choose a city instead.";
}

export function CityCombobox({
  value,
  onChange,
  label = "Home city",
  autoFocus = false,
  required = false,
  className = "",
}: CityComboboxProps) {
  const generatedId = useId();
  const inputId = `city-${generatedId}`;
  const listboxId = `${inputId}-suggestions`;
  const statusId = `${inputId}-status`;
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [locating, setLocating] = useState(false);
  const [status, setStatus] = useState("");
  const [statusError, setStatusError] = useState(false);
  const mounted = useRef(true);
  const reverseLookup = useRef<AbortController | null>(null);
  const activeOptionRef = useRef<HTMLButtonElement | null>(null);
  const suggestions = useMemo(() => findCities(value), [value]);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      reverseLookup.current?.abort();
    };
  }, []);

  useEffect(() => {
    activeOptionRef.current?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  function chooseCity(option: CityOption) {
    onChange(formatCity(option));
    setOpen(false);
    setActiveIndex(-1);
    setStatus("");
    setStatusError(false);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((index) => Math.min(index + 1, suggestions.length - 1));
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((index) => (index <= 0 ? suggestions.length - 1 : index - 1));
      return;
    }
    if (event.key === "Enter" && open && activeIndex >= 0 && suggestions[activeIndex]) {
      event.preventDefault();
      chooseCity(suggestions[activeIndex]);
      return;
    }
    if (event.key === "Escape") {
      setOpen(false);
      setActiveIndex(-1);
    }
  }

  function handleBlur(event: FocusEvent<HTMLDivElement>) {
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
      setOpen(false);
      setActiveIndex(-1);
    }
  }

  function useCurrentLocation() {
    setOpen(false);
    setActiveIndex(-1);
    setStatus("");
    setStatusError(false);

    if (!navigator.geolocation) {
      setStatus("Location is not available in this browser. Search and choose a city instead.");
      setStatusError(true);
      return;
    }

    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        if (!mounted.current) return;
        reverseLookup.current?.abort();
        const controller = new AbortController();
        reverseLookup.current = controller;
        void reverseGeocodeCity(position.coords.latitude, position.coords.longitude, controller.signal)
          .then((cityName) => {
            if (!mounted.current) return;
            onChange(cityName);
            setStatus(`Home city set to ${cityName}.`);
            setStatusError(false);
          })
          .catch((error: unknown) => {
            if (error instanceof DOMException && error.name === "AbortError") return;
            if (!mounted.current) return;
            setStatus(error instanceof Error ? error.message : "Roam could not match your location to a city.");
            setStatusError(true);
          })
          .finally(() => {
            if (reverseLookup.current === controller) reverseLookup.current = null;
            if (mounted.current) setLocating(false);
          });
      },
      (error) => {
        if (!mounted.current) return;
        setLocating(false);
        setStatus(locationErrorMessage(error));
        setStatusError(true);
      },
      { enableHighAccuracy: false, timeout: 10_000, maximumAge: 600_000 },
    );
  }

  const activeOption = open && activeIndex >= 0 ? `${listboxId}-${suggestions[activeIndex]?.id}` : undefined;

  return (
    <div className={`field city-combobox ${className}`.trim()} onBlur={handleBlur}>
      <label htmlFor={inputId}>{label}</label>
      <div className="city-input-wrap">
        <input
          id={inputId}
          role="combobox"
          aria-autocomplete="list"
          aria-controls={listboxId}
          aria-describedby={statusId}
          aria-expanded={open}
          aria-activedescendant={activeOption}
          autoComplete="off"
          autoFocus={autoFocus}
          required={required}
          value={value}
          onChange={(event) => {
            onChange(event.target.value);
            setStatus("");
            setStatusError(false);
            setOpen(true);
            setActiveIndex(-1);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder="Search cities or type your own"
        />
        {open ? (
          <div className="city-suggestions" id={listboxId} role="listbox" aria-label="City suggestions">
            {suggestions.length > 0 ? suggestions.map((option, index) => (
              <button
                id={`${listboxId}-${option.id}`}
                key={option.id}
                ref={index === activeIndex ? activeOptionRef : undefined}
                type="button"
                role="option"
                aria-selected={index === activeIndex}
                className={index === activeIndex ? "is-active" : ""}
                onMouseDown={(event) => event.preventDefault()}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => chooseCity(option)}
              >
                <span>{option.name}</span>
                <small>{option.country}</small>
              </button>
            )) : (
              <p className="city-no-results">No list match. You can keep the city name you typed.</p>
            )}
          </div>
        ) : null}
      </div>
      <div className="city-location-row">
        <button className="city-location-button" type="button" disabled={locating} onClick={useCurrentLocation}>
          <Icon name="locate" size={16} />
          {locating ? "Finding your city…" : "Use my location"}
        </button>
        <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">Location names by OpenStreetMap</a>
      </div>
      <p className={`city-status ${statusError ? "is-error" : ""}`.trim()} id={statusId} role="status" aria-live="polite">{status}</p>
    </div>
  );
}
